import React, { useEffect, useMemo, useState } from "react";

/* ============================ Utils ============================ */

const keynorm = (s) =>
  String(s ?? "")
    .toLowerCase()
    .replace(/\./g, "")
    .replace(/\b(jr|sr|ii|iii|iv)\b/g, "$1")
    .replace(/[^a-z0-9\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

const namesEqual = (a, b) => {
  const A = keynorm(a);
  const B = keynorm(b);
  if (!A || !B) return false;
  if (A === B) return true;
  const pad = (x) => ` ${x} `;
  return pad(A).includes(pad(B)) || pad(B).includes(pad(A));
};

const cleanNumeric = (v) => {
  const s = String(v ?? "");
  const stripped = s.replace(/[^\d.-]/g, "");
  if (!stripped || stripped === "-" || stripped === "." || stripped === "-.") return null;
  const n = Number(stripped);
  return Number.isFinite(n) ? n : null;
};

const toMoney = (v) => {
  const n = cleanNumeric(v);
  if (n == null) return "—";
  return n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 });
};
const toPct = (v) => {
  const n = cleanNumeric(v);
  if (n == null) return "—";
  return `${n}%`;
};
const toNum = (v) => {
  const n = cleanNumeric(v);
  return n == null ? null : n;
};

const imgOk = () => {};
const imgHideOnError = (e) => { if (e?.currentTarget) e.currentTarget.style.display = "none"; };

const normalizeKey = (k) =>
  String(k ?? "").toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim();

const buildKeyMap = (row = {}) => {
  const map = new Map();
  Object.keys(row || {}).forEach((k) => map.set(normalizeKey(k), k));
  return map;
};
const resolveKey = (map, patterns) => {
  for (const p of patterns) {
    const hit = [...map.keys()].find((nk) => p.test(nk));
    if (hit) return map.get(hit);
  }
  return null;
};

/* ============================ Fetch ============================ */

function useJson(url, go = true) {
  const [data, setData] = useState(null);
  useEffect(() => {
    let alive = true;
    if (!url || !go) return;
    (async () => {
      try {
        const r = await fetch(url, { cache: "no-store" });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json();
        if (alive) setData(j);
      } catch {
        if (alive) setData(null);
      }
    })();
    return () => { alive = false; };
  }, [url, go]);
  return data;
}

/* ============================ UI ============================ */

const Backdrop = ({ show }) =>
  show ? (
    <div className="fixed inset-0 z-[999] bg-slate-950/50 backdrop-blur-sm" />
  ) : null;

// lighter glass gradient + click-outside-to-close
const Shell = ({ children, onOutsideClick }) => (
  <div
    className="fixed z-[1000] inset-0 flex items-start justify-center p-4 md:p-8 overflow-y-auto"
    role="dialog"
    aria-modal="true"
    onClick={onOutsideClick}
  >
    <div
      className="relative w-full max-w-5xl rounded-3xl border border-white/10 bg-gradient-to-br from-white/90 to-white/70 dark:from-slate-900/60 dark:to-slate-900/30 backdrop-blur-xl shadow-[0_10px_60px_-10px_rgba(0,0,0,0.45)] ring-1 ring-black/5 overflow-hidden animate-in fade-in duration-150"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="pointer-events-none absolute -inset-px rounded-3xl bg-[radial-gradient(60%_60%_at_20%_10%,rgba(59,130,246,0.18),transparent_60%),radial-gradient(40%_40%_at_90%_10%,rgba(236,72,153,0.18),transparent_60%)]" />
      {children}
    </div>
  </div>
);

const CloseIcon = (props) => (
  <svg viewBox="0 0 24 24" aria-hidden="true" className={props.className}>
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const Header = ({ carNumSrc, title, onClose, subtitle }) => (
  <div className="relative z-10 flex items-center justify-between px-6 py-5 border-b border-white/20 dark:border-white/10 bg-gradient-to-r from-slate-50/70 to-white/30 dark:from-slate-900/35 dark:to-transparent">
    <div className="flex items-center gap-4">
      {carNumSrc ? (
        <img
          src={carNumSrc}
          className="h-9 w-9 object-contain drop-shadow"
          onLoad={imgOk}
          onError={imgHideOnError}
          alt=""
        />
      ) : null}
      <div>
        <h2 className="text-2xl font-extrabold tracking-tight text-slate-900 dark:text-white">{title}</h2>
        {subtitle ? <p className="text-xs font-medium text-slate-600 dark:text-slate-400 mt-0.5">{subtitle}</p> : null}
      </div>
    </div>
    <button
      aria-label="Close"
      onClick={onClose}
      className="h-9 w-9 grid place-items-center rounded-full text-slate-600 hover:text-slate-900 hover:bg-white/80 dark:text-slate-300 dark:hover:text-white dark:hover:bg-slate-800/70 transition-colors"
    >
      <CloseIcon className="h-5 w-5" />
    </button>
  </div>
);

/* unified (slightly brighter) glass card style */
const cardBase =
  "rounded-2xl ring-1 ring-white/15 bg-white/15 dark:bg-slate-900/25 backdrop-blur-md transition transform hover:-translate-y-0.5 hover:shadow-md";

const StatCard = ({ label, value }) => (
  <div className={`${cardBase} p-3.5`}>
    <div className="text-[10px] uppercase tracking-wider text-slate-700/80 dark:text-slate-300/80 font-semibold">
      {label}
    </div>
    <div className="mt-1.5 text-2xl font-extrabold text-slate-900 dark:text-white tabular-nums">
      {value ?? "—"}
    </div>
  </div>
);

/* ============================ MAIN ============================ */

export default function DriverCardModal({
  open,
  onClose,
  row,
  driverNum,
  practiceAvg,
  practiceUrl,
  series,             // optional; auto-detected from URL if omitted
  debug = false,
  bases = {
    base: "/",
    numLogoBase: undefined,
    driverPhotoBase: undefined,
    teamLogoBase: undefined,
  },
}) {
  const show = !!open && !!row;
  const base = bases?.base ?? "/";

  const inferSeries = () => {
    const p = (typeof window !== "undefined" ? window.location.pathname : "").toLowerCase();
    if (p.includes("xfinity")) return "xfinity";
    if (p.includes("truck")) return "trucks";
    return "cup";
  };
  const seriesSlug = (series || inferSeries()).toLowerCase();
  const dataRoot = `${base}data/nascar/${seriesSlug}/latest`;

  const numLogoBase = bases?.numLogoBase ?? `/logos/nascar/${seriesSlug}`;
  const driverPhotoBase = bases?.driverPhotoBase ?? `/logos/nascar/Drivers`;
  const teamLogoBase = bases?.teamLogoBase ?? `/logos/nascar/Teams`;

  const projKeyMap = useMemo(() => buildKeyMap(row || {}), [row]);

  const nameKey =
    resolveKey(projKeyMap, [/^driver\b/, /^driver\s*name$/, /^name$/]) || "Driver";
  const driverName = String(row?.[nameKey] ?? "");

  const carKey =
    resolveKey(projKeyMap, [/^car$/, /^no$/, /^number$/, /^car\s*#$/, /^car\s*no$/, /^car\s*number$/]) ||
    "Car #";
  const carNum = (() => {
    const n = cleanNumeric(row?.[carKey]);
    return n == null ? null : n;
  })();

  const dkSalKey =
    resolveKey(projKeyMap, [/^dk\s*sal$/, /\bdk\b.*sal/, /^salary.*dk$/, /^dk.*salary$/, /^salary$/]) || null;
  const fdSalKey =
    resolveKey(projKeyMap, [/^fd\s*sal$/, /\bfd\b.*sal/, /^salary.*fd$/, /^fd.*salary$/]) || null;

  const dkProjKey =
    resolveKey(projKeyMap, [/^dk\b.*proj$/, /\bproj\b.*dk$/, /^dk\s*proj$/, /^dk$/, /^dk\s*points?$/]) || null;
  const fdProjKey =
    resolveKey(projKeyMap, [/^fd\b.*proj$/, /\bproj\b.*fd$/, /^fd\s*proj$/, /^fd$/, /^fd\s*points?$/]) || null;

  const dkOwnKey =
    resolveKey(projKeyMap, [/^dk\b.*(own|pown)/, /(own|pown).*dk/, /^dk\s*(own|pown)$/, /^dk\s*%own$/, /^dk\s*ownership$/]) || null;
  const fdOwnKey =
    resolveKey(projKeyMap, [/^fd\b.*(own|pown)/, /(own|pown).*fd/, /^fd\s*(own|pown)$/, /^fd\s*%own$/, /^fd\s*ownership$/]) || null;

  const qualKey = resolveKey(projKeyMap, [/^qpos$/, /^q\s*pos$/, /qual/, /^pos\s*qual$/]) || null;

  /* series data: team lookup */
  const data = useJson(`${dataRoot}/data.json`, show);
  const teamFromData = useMemo(() => {
    if (!data) return null;
    const arr = Array.isArray(data)
      ? data
      : Array.isArray(data?.rows)
      ? data.rows
      : Array.isArray(data?.data)
      ? data.data
      : [];
    if (!arr.length) return null;

    const driverKeys = new Set();
    const carKeys = new Set();
    const teamKeys = new Set();
    for (const r of arr) {
      for (const k of Object.keys(r)) {
        const nk = k.toLowerCase().trim();
        if (/^driver\b/.test(nk) || /^name$/.test(nk)) driverKeys.add(k);
        if (/^car/.test(nk) || /^no$/.test(nk) || /^number$/.test(nk) || /^car\s*#/.test(nk)) carKeys.add(k);
        if (/^team/.test(nk) || /^owner$/.test(nk) || /^organization$/.test(nk) || /^org$/.test(nk)) teamKeys.add(k);
      }
    }

    const getTeam = (r) => {
      for (const k of teamKeys) {
        const v = r?.[k];
        if (v != null && String(v).trim() !== "") return String(v);
      }
      return null;
    };

    if (driverName) {
      for (const r of arr) {
        for (const k of driverKeys) {
          if (namesEqual(r?.[k], driverName)) {
            const t = getTeam(r);
            if (t) return t;
          }
        }
      }
    }

    if (carNum != null) {
      for (const r of arr) {
        for (const k of carKeys) {
          const n = cleanNumeric(r?.[k]);
          if (n != null && n === carNum) {
            const t = getTeam(r);
            if (t) return t;
          }
        }
      }
    }

    return null;
  }, [data, driverName, carNum]);

  /* practice avg */
  const practiceUrlEff = practiceUrl || `${dataRoot}/practice_cons.json`;
  const practiceRaw = useJson(practiceUrlEff, show && practiceAvg == null);
  const practiceAverageLocal = useMemo(() => {
    if (practiceAvg != null && practiceAvg !== "") return String(practiceAvg);
    const arr = Array.isArray(practiceRaw)
      ? practiceRaw
      : Array.isArray(practiceRaw?.rows)
      ? practiceRaw.rows
      : Array.isArray(practiceRaw?.data)
      ? practiceRaw.data
      : [];
    if (!arr.length || !driverName) return null;

    const nameKeys = new Set();
    const avgKeys = new Set();
    for (const k of Object.keys(arr[0] || {})) {
      const nk = k.toLowerCase().trim();
      if (/^driver\b/.test(nk) || /^name$/.test(nk)) nameKeys.add(k);
      if (/^average$/.test(nk) || /^avg$/.test(nk) || /lap\s*avg/.test(nk)) avgKeys.add(k);
    }

    let hit = null;
    for (const r of arr) {
      for (const nk of nameKeys) {
        if (namesEqual(r?.[nk], driverName)) {
          hit = r;
          break;
        }
      }
      if (hit) break;
    }

    if (!hit) return null;
    for (const ak of avgKeys) {
      const n = cleanNumeric(hit?.[ak]);
      if (n != null) return n.toFixed(1);
    }
    return null;
  }, [practiceRaw, practiceAvg, driverName]);

  /* images */
  const displayNum = Number.isFinite(driverNum) ? driverNum : carNum; // no corner chip
  const carIconSrc = Number.isFinite(displayNum) ? `${numLogoBase}/${displayNum}.png` : null;
  const driverSlug = driverName ? driverName.toLowerCase().replace(/[^a-z0-9]+/g, "_") : "";
  const driverPhotoSrc = driverSlug ? `${driverPhotoBase}/${driverSlug}.png` : null;

  const teamNameForLogo = (teamFromData || String(row?.Team ?? "")).trim();
  const teamFile = teamNameForLogo && teamNameForLogo.replace(/\s+/g, "_").replace(/[^A-Za-z0-9_]/g, "");
  const teamLogoSrc = teamFile ? `${teamLogoBase}/${teamFile}.png` : null;

  /* values */
  const teamDisplay = teamFromData || row?.Team || "—";
  const qualDisplay = qualKey ? row?.[qualKey] ?? "—" : "—";
  const dkSalary = dkSalKey ? row?.[dkSalKey] : undefined;
  const fdSalary = fdSalKey ? row?.[fdSalKey] : undefined;
  const dkProj = dkProjKey ? row?.[dkProjKey] : undefined;
  const fdProj = fdProjKey ? row?.[fdProjKey] : undefined;
  const dkOwn = dkOwnKey ? row?.[dkOwnKey] : undefined;
  const fdOwn = fdOwnKey ? row?.[fdOwnKey] : undefined;

  if (!show) return null;

  return (
    <>
      <Backdrop show={show} />
      <Shell onOutsideClick={onClose}>
        <Header
          carNumSrc={carIconSrc}
          title={driverName || "Driver"}
          subtitle={teamDisplay && teamDisplay !== "—" ? teamDisplay : ""}
          onClose={onClose}
        />

        <div className="relative z-10 p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Left: Photo + Team logo */}
            <div className="md:col-span-1">
              <div className={`relative ${cardBase} aspect-[4/3] overflow-hidden grid place-items-center`}>
                <div
                  className="pointer-events-none absolute -inset-10 blur-2xl opacity-30"
                  style={{
                    background:
                      "radial-gradient(60% 60% at 50% 20%, rgba(59,130,246,.25), transparent), radial-gradient(60% 60% at 60% 60%, rgba(236,72,153,.18), transparent)",
                  }}
                />
                {driverPhotoSrc ? (
                  <img
                    src={driverPhotoSrc}
                    className="relative h-full w-full object-contain p-6 drop-shadow-[0_10px_25px_rgba(0,0,0,0.2)]"
                    onLoad={imgOk}
                    onError={imgHideOnError}
                    alt=""
                  />
                ) : (
                  <div className="relative text-slate-400">No photo</div>
                )}
              </div>

              <div className="mt-4 flex items-center justify-center">
                {teamLogoSrc ? (
                  <img
                    src={teamLogoSrc}
                    className="h-16 w-auto max-h-20 object-contain drop-shadow"
                    onLoad={imgOk}
                    onError={imgHideOnError}
                    alt=""
                  />
                ) : null}
              </div>
            </div>

            {/* Right: Stats grid */}
            <div className="md:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
              <StatCard label="TEAM" value={teamDisplay} />
              <StatCard label="QUAL POS" value={qualDisplay ?? "—"} />
              <StatCard label="PRACTICE" value={practiceAverageLocal ?? "—"} />
              <StatCard label="DK SALARY" value={toMoney(dkSalary)} />
              <StatCard label="FD SALARY" value={toMoney(fdSalary)} />
              <StatCard label="DK PROJ" value={toNum(dkProj) ?? "—"} />
              <StatCard label="FD PROJ" value={toNum(fdProj) ?? "—"} />
              <StatCard label="DK OWN" value={toPct(dkOwn)} />
              <StatCard label="FD OWN" value={toPct(fdOwn)} />
            </div>
          </div>

          {debug ? (
            <div className="mt-6 text-xs text-slate-600/90 dark:text-slate-300/80 border-t border-white/30 dark:border-white/10 pt-4">
              <div><b>series</b>: {seriesSlug}</div>
              <div><b>nameKey</b>: {String(nameKey)}</div>
              <div><b>carKey</b>: {String(carKey)}</div>
              <div><b>dkSalKey</b>: {String(dkSalKey)}</div>
              <div><b>fdSalKey</b>: {String(fdSalKey)}</div>
              <div><b>dkProjKey</b>: {String(dkProjKey)}</div>
              <div><b>fdProjKey</b>: {String(fdProjKey)}</div>
              <div><b>dkOwnKey</b>: {String(dkOwnKey)}</div>
              <div><b>fdOwnKey</b>: {String(fdOwnKey)}</div>
              <div><b>teamFromData</b>: {String(teamFromData)}</div>
            </div>
          ) : null}

          <div className="mt-6 flex justify-end">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-xl font-semibold text-slate-800 dark:text-white bg-white/90 hover:bg-white shadow-sm ring-1 ring-slate-200 dark:bg-slate-800/70 dark:hover:bg-slate-800 dark:ring-slate-700 transition"
            >
              Close
            </button>
          </div>
        </div>
      </Shell>
    </>
  );
}
